import { FONTS } from "./assets/fonts";
import { STANDARD_FONT_WEIGHTS, ZUSATZ_SCHNITTE, googleNamenRegeln, teamSchriftRegel } from "../assets/fonts/index";

/**
 * Standard-Schriften des Pro-Editors als @font-face fuer den READER (Jans
 * Komplex-Scrolly 01.08.2026: "Alle Schriften in den Infografiken / Karten
 * sehen nicht so aus, wie sie gebaut wurden").
 *
 * Hintergrund: HTML-Infografiken/Karten (DOM-Overlay) und HTML-Boxen tragen
 * Familiennamen wie 'BarlowSemiCondensed'. Im EDITOR injiziert Storyforger
 * dafuer @font-face-Regeln (standardFonts.ts) — auf der Viewer-Seite tat das
 * niemand, jede Beschriftung fiel auf den Browser-Default zurueck. (Die
 * 3D-FLAECHEN-Infografik war nie betroffen: sie bettet die Schrift als
 * data:-URI direkt ins SVG ein, siehe infographicMesh.embeddedFontCss.)
 *
 * Die URLs kommen aus der FONTS-Map des Renderer-Pakets — dieselben
 * gebuendelten Dateien, die Troika (3D-Text) und die SVG-Einbettung nutzen.
 * Vite loest sie in JEDEM Konsumenten korrekt auf (App wie Viewer); es sind
 * keine kopierten Static-Dateien noetig. Inklusive der Gravur-Aliasse fuer
 * Alt-Scrollys. Idempotent; im Editor harmlos-doppelt (identische Dateien).
 */
const STYLE_ELEMENT_ID = "scrolly-reader-standard-fonts";

export const injectReaderStandardFonts = (): void => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ELEMENT_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    // Familien mit mehreren Schnitten (STANDARD_FONT_WEIGHTS, z. B. Roboto
    // 400/700/900) bekommen je Gewicht eine Regel — sonst fettet der Browser
    // kuenstlich (Jans Fund am Startbildschirm, 07.09.2026).
    style.textContent = Object.entries(FONTS)
        .flatMap(([family, url]) => {
            const schnitte = STANDARD_FONT_WEIGHTS[family];
            if (schnitte?.length) {
                return [...schnitte, ...(ZUSATZ_SCHNITTE[family] ?? [])].map(
                    (s) =>
                        `@font-face { font-family: '${family}'; src: url('${s.url}'); font-weight: ${s.weight}; font-display: swap; }`
                );
            }
            // Bisherige Regel unverändert, dahinter die übrigen Schnitte (06.10.2026)
            return [
                `@font-face { font-family: '${family}'; src: url('${url}'); font-display: swap; }`,
                ...(ZUSATZ_SCHNITTE[family] ?? []).map(
                    (s) =>
                        `@font-face { font-family: '${family}'; src: url('${s.url}'); font-weight: ${s.weight}; font-display: swap; }`
                ),
            ];
        })
        // Dieselben Dateien unter den Google-Namen (06.10.2026, s. GOOGLE_NAMEN)
        .concat(googleNamenRegeln())
        .join("\n");
    document.head.appendChild(style);
};

const TEAM_STYLE_ELEMENT_ID = "scrolly-reader-team-fonts";

/**
 * Team-/Brandschriften des Scrollys als @font-face registrieren (Jans
 * Schriften-Vereinheitlichung 03.08.2026).
 *
 * Quelle sind die im Scrolly hinterlegten `slideOptions.fontInfos`
 * (familyName → fontUrl), die der Editor beim Anwenden einer Team-Schrift
 * schreibt — 3D-Text UND HTML-Fenster. Der 3D-Pfad braucht das nicht (Troika
 * laedt die Datei selbst ueber die URL), die HTML-Fenster im Reader dagegen
 * schon: ohne @font-face faellt eine Team-Schrift dort still auf die
 * Ersatzschrift zurueck. Idempotent + additiv (mehrfach aufrufbar, z. B. wenn
 * der Reader spaeter weitere Scrollys nachlaedt).
 */
export const injectReaderTeamFonts = (
    fontInfos: Array<{ familyName?: string; fontUrl?: string; weight?: number; italic?: boolean }> | undefined
): void => {
    if (typeof document === "undefined") return;
    if (!Array.isArray(fontInfos) || fontInfos.length === 0) return;
    let style = document.getElementById(TEAM_STYLE_ELEMENT_ID);
    if (!style) {
        style = document.createElement("style");
        style.id = TEAM_STYLE_ELEMENT_ID;
        document.head.appendChild(style);
    }
    const vorhanden = style.textContent || "";
    const neue = fontInfos
        .filter((f) => f?.familyName && f?.fontUrl)
        .map((f) => teamSchriftRegel(f as { familyName: string; fontUrl: string; weight?: number; italic?: boolean }))
        .filter((regel) => !vorhanden.includes(regel));
    if (neue.length === 0) return;
    style.textContent = [vorhanden, ...neue].filter(Boolean).join("\n");
};

